import AppointmentDoneSign from 'components/planning/AppointmentDoneSign';
import { appointmentColorOf } from 'constants/appointmentColors';
import { EVENT_CATEGORIES, INTERNAL_CATEGORY, eventCategoryOf } from 'constants/eventCategories';
import joinDetails from 'helper/joinDetails';
import { clockLabel, durationLabel, shortDayLabel } from 'helper/timeLabels';
import useHomeText from 'hooks/useHomeText';
import useText from 'hooks/useText';
import { appointmentAccent, appointmentDays } from 'pages/planning/planningItems';

// The bar beside an appointment, as its block in the planning: the colour of
// its colorId, else of its category; white and outlined while the caller has
// not answered, dashed when they may come, lighter when the slot stays available.
const barOf = (appointment, colours) => {
  if (appointment.masked) return { className: 'w-[3px] bg-gray-300', style: undefined };
  const accent = appointmentAccent(appointment);
  const faded = appointment.transparency === 'transparent' ? 'opacity-50' : '';
  if (appointment.myResponse === 'needsAction') return { className: `w-[3px] border bg-white ${faded}`, style: { borderColor: accent } };
  if (appointment.myResponse === 'tentative') return { className: `w-0 border-l-[3px] border-dashed ${faded}`, style: { borderColor: accent } };
  if (appointmentColorOf(appointment.colorId)) return { className: `w-[3px] ${faded}`, style: { backgroundColor: accent } };
  return { className: `w-[3px] ${colours.bar} ${faded}`, style: undefined };
};

/**
 * An appointment of the day, as the planning lists it: its time and length,
 * the bar and tag of its category, the company (an internal meeting shows its
 * own title) and who and where. A done appointment fades and shows a check.
 * A whole day says so in place of its time; one over several days gives its
 * start and its last day.
 * A masked one (private, not the caller's) only says « Occupé », in grey.
 * Every appointment, Google's and the masked ones included, calls `onOpen`.
 */
const AppointmentItem = ({ appointment, onOpen }) => {
  const { categoryLabel, text } = useHomeText();
  const { text: planningText } = useText('planning');
  const category = eventCategoryOf(appointment.category);
  const colours = EVENT_CATEGORIES[category];
  const start = new Date(appointment.startsAt);
  const minutes = Math.round((new Date(appointment.endsAt) - start) / 60000);
  const internal = category === INTERNAL_CATEGORY;
  const masked = Boolean(appointment.masked);
  const bar = barOf(appointment, colours);
  let title = (internal ? appointment.title : appointment.company?.name || appointment.title) || planningText('untitled');
  if (masked) title = planningText('busy');
  const days = appointment.allDay ? [] : appointmentDays(appointment);
  const details = masked
    ? ''
    : internal
    ? appointment.description
    : joinDetails([
        appointment.contactName || appointment.company?.contactName,
        category !== 'prospection' && category !== 'visite_client' && appointment.description
          ? appointment.description
          : appointment.location,
      ]);
  let when = (
    <>
      <span className="text-sm font-semibold leading-5 text-gray-900">{clockLabel(start)}</span>
      <span className="text-xs leading-4 text-gray-600">{durationLabel(minutes)}</span>
    </>
  );
  if (appointment.allDay) when = <span className="text-xs font-semibold leading-4 text-gray-900">{text('agenda.allDay')}</span>;
  else if (days.length > 1) {
    when = (
      <>
        <span className="text-sm font-semibold leading-5 text-gray-900">{clockLabel(start)}</span>
        <span className="text-xs leading-4 text-gray-600">{text('agenda.until', { day: shortDayLabel(days[days.length - 1]) })}</span>
      </>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpen(appointment)}
      className={`relative -mx-2 flex shrink-0 gap-3 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-gray-50 ${
        appointment.status === 'done' ? 'opacity-60' : ''
      }`}
    >
      <span className="flex w-11 shrink-0 flex-col">{when}</span>
      <span data-testid="appointment-bar" className={`shrink-0 self-stretch rounded-sm ${bar.className}`} style={bar.style} />
      <span className={`flex min-w-0 flex-1 flex-col items-start gap-1 ${appointment.status === 'done' ? 'pr-4' : ''}`}>
        {!masked && <span className={`rounded px-2 py-0.5 text-xs font-medium ${colours.tag}`}>{categoryLabel(category)}</span>}
        <span className={`text-sm font-medium leading-5 ${masked ? 'text-gray-600' : 'text-gray-900'}`}>{title}</span>
        {details && <span className="text-xs leading-4 text-gray-600">{details}</span>}
      </span>
      {appointment.status === 'done' && <AppointmentDoneSign />}
    </button>
  );
};

export default AppointmentItem;
